import FootTrafficFilterApi from '@/Enums/Attribution/FootTraffic/FilterApi.enum';
import FootTrafficFilterType from '@/Enums/Attribution/FootTraffic/FilterType.enum';
import FootTrafficReportType from '@/Enums/Attribution/FootTraffic/ReportType.enum';
import type {
  IFootTrafficCampaignFilterOptionsResponse,
  IFootTrafficImpressionsReports,
  IFootTrafficLineItemFilterOptionsResponse,
  IFootTrafficLocationFilterOptionsResponse,
  IFootTrafficVisitsReports,
} from '@/Interfaces/Attribution/FootTraffic.interface';
import type {
  IFootTrafficCampaignControlOption,
  IFootTrafficLineItemControlOption,
  IFootTrafficState,
  IFootTrafficVenueCoverageResponse,
} from '@/Interfaces/Attribution/State.interface';
import type { IControlOption } from '@/Interfaces/ControlOption.interface';
import type { IAbortSignalThunkArg, IRejectValueThunkConfig } from '@/Interfaces/Slices/AsyncThunk.interface';
import FootTrafficService from '@/Services/FootTraffic/FootTraffic.service';
import FootTrafficFilterService from '@/Services/FootTraffic/FootTrafficFilter.service';
import { DEFAULT_LOADING_REQUEST_ID } from '@/constants/slice';
import {
  footTrafficFilterRequestHandler,
  footTrafficRequestHandler,
  fulfilledFootTrafficCaseHandler,
} from '@/helpers/attribution/footTraffic';
import { getControlOptionsValues } from '@/helpers/filters/filterMapping';
import type { RootState } from '@/store';
import { fetchFootTrafficVenueCoverage } from '@/store/api/attribution.api';
import { fulfilledCaseHandler, pendingCaseHandler, rejectedCaseHandler } from '@/store/asyncThunkHandlers';
import { selectFootTrafficFilters } from '@/store/selectors/attribution/footTraffic.selectors';
import type { FootTrafficFilters } from '@/types/Attribution/filters.types';
import type { RequestMap } from '@/types/RequestMap.types';
import { type PayloadAction, createAsyncThunk, createSlice } from '@reduxjs/toolkit';

enum ActionPrefix {
  VisitsReports = 'footTraffic/visitsReports',
  ImpressionsReports = 'footTraffic/impressionsReports',
  CampaignsFilterOptions = 'footTraffic/campaignsFilterOptions',
  LocationFilterOptions = 'footTraffic/locationFilterOptions',
  LineItemFilterOptions = 'footTraffic/lineItemFilterOptions',
  VenueCoverage = 'footTraffic/venueCoverage',
}

const defaultVenueCoverage: IFootTrafficVenueCoverageResponse = {
  summary: { total: 0, withData: 0, withoutData: 0 },
  locations: [],
};

const initialState: IFootTrafficState = {
  filters: FootTrafficService.getDefaultFootTrafficFilter(),
  channels: Object.values(FootTrafficFilterApi),
  footTrafficVisitsReportsRequestId: DEFAULT_LOADING_REQUEST_ID,
  ...FootTrafficService.getDefaultVisitsReports(),
  footTrafficImpressionsReportsRequestId: DEFAULT_LOADING_REQUEST_ID,
  ...FootTrafficService.getDefaultImpressionsReports(),

  campaigns: [],
  campaignsRequestId: DEFAULT_LOADING_REQUEST_ID,
  locations: [],
  locationsRequestId: DEFAULT_LOADING_REQUEST_ID,
  lineItems: [],
  lineItemsRequestId: DEFAULT_LOADING_REQUEST_ID,

  venueCoverage: defaultVenueCoverage,
  venueCoverageRequestId: DEFAULT_LOADING_REQUEST_ID,
};

const footTrafficSlice = createSlice({
  name: 'footTraffic',
  initialState,
  reducers: {
    setFilters: (state, action: PayloadAction<Partial<FootTrafficFilters>>) => {
      state.filters = {
        ...state.filters,
        ...action.payload,
      };
    },
    resetFilters: state => {
      state.filters = FootTrafficService.getDefaultFootTrafficFilter();
    },
    setChannels: (state, action: PayloadAction<FootTrafficFilterApi[]>) => {
      state.channels = action.payload;
    },
    resetFootTrafficPageState: () => initialState,
  },
  extraReducers: builder => {
    footTrafficFilterOptionsMap.forEach(([key, thunk]) => {
      builder
        .addCase(thunk.pending, pendingCaseHandler(key, []))
        .addCase(thunk.rejected, rejectedCaseHandler(key, []))
        .addCase(thunk.fulfilled, fulfilledCaseHandler(key));
    });

    builder
      .addCase(getFootTrafficVisitsReports.pending, pendingCaseHandler('footTrafficVisitsReports'))
      .addCase(getFootTrafficVisitsReports.rejected, rejectedCaseHandler('footTrafficVisitsReports'))
      .addCase(getFootTrafficVisitsReports.fulfilled, fulfilledFootTrafficCaseHandler('footTrafficVisitsReports'))
      .addCase(getFootTrafficImpressionsReports.pending, pendingCaseHandler('footTrafficImpressionsReports'))
      .addCase(getFootTrafficImpressionsReports.rejected, rejectedCaseHandler('footTrafficImpressionsReports'))
      .addCase(
        getFootTrafficImpressionsReports.fulfilled,
        fulfilledFootTrafficCaseHandler('footTrafficImpressionsReports'),
      )
      .addCase(getFootTrafficVenueCoverage.pending, pendingCaseHandler('venueCoverage', defaultVenueCoverage))
      .addCase(getFootTrafficVenueCoverage.rejected, rejectedCaseHandler('venueCoverage', defaultVenueCoverage))
      .addCase(getFootTrafficVenueCoverage.fulfilled, fulfilledCaseHandler('venueCoverage'));
  },
});

export default footTrafficSlice.reducer;

export const { setFilters, resetFilters, resetFootTrafficPageState, setChannels } = footTrafficSlice.actions;

export const getFootTrafficVisitsReports = createAsyncThunk<
  IFootTrafficVisitsReports,
  IAbortSignalThunkArg,
  IRejectValueThunkConfig
>(
  ActionPrefix.VisitsReports,
  footTrafficRequestHandler<IFootTrafficVisitsReports>(
    FootTrafficReportType.VisitsReports,
    FootTrafficService.getDefaultVisitsReports(),
  ),
);

export const getFootTrafficImpressionsReports = createAsyncThunk<
  IFootTrafficImpressionsReports,
  IAbortSignalThunkArg,
  IRejectValueThunkConfig
>(
  ActionPrefix.ImpressionsReports,
  footTrafficRequestHandler<IFootTrafficImpressionsReports>(
    FootTrafficReportType.ImpressionsReports,
    FootTrafficService.getDefaultImpressionsReports(),
  ),
);

export const getFootTrafficLocationOptions = createAsyncThunk<IControlOption[], IAbortSignalThunkArg>(
  ActionPrefix.LocationFilterOptions,
  footTrafficFilterRequestHandler<IFootTrafficLocationFilterOptionsResponse, IControlOption[]>(
    FootTrafficFilterType.Locations,
    FootTrafficFilterService.getDefaultFootTrafficLocationOptions(),
    FootTrafficFilterService.processLocationFilterOptions,
  ),
);

export const getFootTrafficLineItemOptions = createAsyncThunk<
  IFootTrafficLineItemControlOption[],
  IAbortSignalThunkArg
>(
  ActionPrefix.LineItemFilterOptions,
  footTrafficFilterRequestHandler<IFootTrafficLineItemFilterOptionsResponse, IFootTrafficLineItemControlOption[]>(
    FootTrafficFilterType.LineItems,
    FootTrafficFilterService.getDefaultFootTrafficLineItemOptions(),
    FootTrafficFilterService.processLineItemFilterOptions,
  ),
);

export const getFootTrafficCampaignOptions = createAsyncThunk<
  IFootTrafficCampaignControlOption[],
  IAbortSignalThunkArg
>(
  ActionPrefix.CampaignsFilterOptions,
  footTrafficFilterRequestHandler<IFootTrafficCampaignFilterOptionsResponse, IFootTrafficCampaignControlOption[]>(
    FootTrafficFilterType.Campaigns,
    FootTrafficFilterService.getDefaultFootTrafficCampaignOptions(),
    FootTrafficFilterService.processCampaignFilterOptions,
  ),
);

export const getFootTrafficVenueCoverage = createAsyncThunk<IFootTrafficVenueCoverageResponse, IAbortSignalThunkArg>(
  ActionPrefix.VenueCoverage,
  async (_payload, { getState }) => {
    const filters = selectFootTrafficFilters(getState() as RootState);
    const advertiserIds = getControlOptionsValues(filters.advertisers);

    if (!advertiserIds.length) {
      return defaultVenueCoverage;
    }

    return await fetchFootTrafficVenueCoverage<IFootTrafficVenueCoverageResponse>({ advertiserIds });
  },
);

export const footTrafficFilterOptionsMap: RequestMap<IFootTrafficState> = [
  ['campaigns', getFootTrafficCampaignOptions],
  ['locations', getFootTrafficLocationOptions],
  ['lineItems', getFootTrafficLineItemOptions],
];
